import React from 'react';
// Vector Capital now uses the same simulator as Vector Capital + (single source
// of truth) so the two pages behave identically.
import VectorCapitalPlusSimulator from '../components/VectorCapitalPlusSimulator.js';
import VectorCapitalPageHeader from '../components/VectorCapitalPageHeader.js';
import { IconLock, IconRefresh } from '../components/Icons';
import { useDelayedTrueWhile } from '../hooks/useDelayedTrueWhile';

const TIER_WAIT_UI_DELAY_MS = 750;

/**
 * Standalone Vector Capital strategy dashboard (side menu: Learn → Vector Capital).
 * Admin tier only.
 */
function VectorCapitalPage({
  user,
  isAdmin,
  authLoading = false,
  tierReady,
  onNavigate,
}) {
  const tierBlocking = !tierReady;
  const showTierWaitUi = useDelayedTrueWhile(tierBlocking, TIER_WAIT_UI_DELAY_MS);
  const hasAccess = Boolean(isAdmin);
  const signedIn = Boolean(user?.email && String(user.email).trim());

  if (!tierReady) {
    if (!showTierWaitUi) return null;
    return (
      <>
        <VectorCapitalPageHeader onNavigate={onNavigate} />
        <div className="vector-capital-page-layout">
          <div className="results-tier-placeholder" role="status" aria-live="polite" aria-busy="true">
            <div className="results-tier-placeholder-inner">
              <span className="results-tier-placeholder-spin" aria-hidden="true">
                <IconRefresh size={24} color="rgba(129, 140, 248, 0.9)" />
              </span>
              <p className="results-tier-placeholder-text">Loading your account access…</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!signedIn) {
    return (
      <div className="feature-gate-stack">
        <VectorCapitalPageHeader onNavigate={onNavigate} />
        <div className="vector-capital-page-layout">
          {authLoading ? (
            <div className="results-tier-placeholder" role="status" aria-live="polite" aria-busy="true">
              <div className="results-tier-placeholder-inner">
                <span className="results-tier-placeholder-spin" aria-hidden="true">
                  <IconRefresh size={24} color="rgba(129, 140, 248, 0.9)" />
                </span>
                <p className="results-tier-placeholder-text">Loading…</p>
              </div>
            </div>
          ) : (
            <div className="upgrade-required-page">
              <div className="upgrade-required-content">
                <div className="pro-lock-icon">
                  <IconLock size={48} />
                </div>
                <h2 className="pro-lock-title">Strategy Simulator</h2>
                <p className="pro-lock-subtitle">Sign in to continue</p>
                <p className="pro-lock-description">
                  Sign in to open the strategy simulator and analysis dashboard.
                </p>
                <button
                  type="button"
                  className="pro-lock-upgrade-button"
                  onClick={() => onNavigate && onNavigate('login')}
                >
                  Sign in
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="feature-gate-stack">
        <VectorCapitalPageHeader onNavigate={onNavigate} />
        <div className="vector-capital-page-layout">
          <div className="upgrade-required-page">
            <div className="upgrade-required-content">
              <div className="pro-lock-icon">
                <IconLock size={48} />
              </div>
              <h2 className="pro-lock-title">Strategy Simulator</h2>
              <p className="pro-lock-subtitle">Admin access only</p>
              <p className="pro-lock-description">
                The strategy simulator is available to RiskLo administrators. Use Overview for your brand dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <VectorCapitalPageHeader onNavigate={onNavigate} />
      <div className="vector-capital-page-layout">
        <VectorCapitalPlusSimulator
          user={user}
          showEquityPortfolios
          isAdmin
          isFullAdmin
        />
      </div>
    </>
  );
}

export default VectorCapitalPage;
